import axios from 'axios'
import { apiClient } from './client'
import type { ApiResponse, PageResponse } from '@/types/api'

/**
 * LC CRM 어드민 API.
 *
 * 리드 변경 계열은 전부 `expectedVersion`을 싣는다. 서버가 `CRM_LEAD_STALE`(409)로 거절하면
 * 화면은 상세를 다시 읽어야 한다 — 그 판단에 필요한 error code를 [apiErrorCode]로 꺼낸다.
 * `handleApiError`는 사람이 읽을 메시지만 주므로 분기 근거로 쓸 수 없다.
 *
 * 백엔드는 null 필드를 JSON에서 **아예 뺀다**. 그래서 nullable 필드는 `T | null`이 아니라
 * 선택 필드로 둔다. `x === null` 같은 엄격 비교는 undefined를 놓쳐 조용히 틀린다 —
 * 실제로 적격성 화면의 "가족 동의"가 이 때문에 `undefined`로 보였다.
 */
export function apiErrorCode(error: unknown): string | undefined {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as ApiResponse<unknown> | undefined
    return body?.error?.code
  }
  return undefined
}

export function isStaleLeadError(error: unknown): boolean {
  return apiErrorCode(error) === 'CRM_LEAD_STALE'
}

function extractFilename(disposition: string | undefined): string | undefined {
  if (!disposition) return undefined
  const star = /filename\*=UTF-8''([^;]+)/.exec(disposition)
  if (star) return decodeURIComponent(star[1])
  const plain = /filename="([^"]+)"/.exec(disposition)
  return plain?.[1]
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue
    search.set(key, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

// ---------------------------------------------------------------- 접수함

/** The acquisition channel belongs to one intake and is independent of sourceType. */
export type AcquisitionChannel = 'WEBSITE' | 'REFERRAL' | 'COLD_DM' | 'IN_PERSON' | 'EXPO' | 'PHONE_DIRECT' | 'OTHER'

export interface InboxListItem {
  intakeId: number
  publicId: string
  contactId: number
  contactName?: string | null
  phoneNormalized: string
  sourceType: string
  acquisitionChannel?: AcquisitionChannel | null
  sourceRefType?: string | null
  applicationStatus: string
  inboxStatus: string
  sessionDate?: string | null
  sessionMode?: string | null
  /** 설명회 참석 여부 (원장 사본): ATTENDED / NO_SHOW / UNKNOWN */
  attendanceStatus: string
  receivedAt: string
  isTest: boolean
}

export interface InboxFilter {
  inboxStatus?: string
  sourceType?: string
  applicationStatus?: string
  attendanceStatus?: string
  sido?: string
  search?: string
  page?: number
  size?: number
}

/** 접수함 단건 상세 — 신청 원본 payload 포함. */
export interface InboxDetail {
  intakeId: number
  publicId: string
  contactId: number
  contactName?: string | null
  phoneNormalized: string
  sourceType: string
  acquisitionChannel?: AcquisitionChannel | null
  sourceRefType?: string | null
  applicationStatus: string
  inboxStatus: string
  sessionDate?: string | null
  sessionMode?: string | null
  attendanceStatus: string
  receivedAt: string
  convertedAt?: string | null
  convertedBy?: number | null
  cancelledAt?: string | null
  isTest: boolean
  sido?: string | null
  sigungu?: string | null
  sourcePayload: Record<string, unknown>
}

export async function fetchIntakeDetail(intakeId: number) {
  const res = await apiClient.get<ApiResponse<InboxDetail>>(`/admin/lc-crm/inbox/${intakeId}`)
  return res.data.data!
}

/**
 * 접수함에서 설명회 참석 여부 기록. 원장 신청이 있으면 서버가 원장에 쓰고 사본이 따라간다
 * (`ledgerSynced=true`). 원장이 없는 접수(옛 신청, 승인 엑셀)는 사본에만 기록된다.
 */
/**
 * 접수 회차(날짜·방식) 수정. 원장 신청이 없는 접수(승인 엑셀·옛 신청)만 허용되고, 원장이 있으면
 * 서버가 `CRM_INTAKE_LEDGER_OWNED`(409)로 거절한다 — 그 경우 문의관리의 회차 이동을 쓴다.
 */
export async function updateIntakeSession(
  intakeId: number,
  payload: { sessionDate?: string; sessionMode?: string },
) {
  const res = await apiClient.patch<ApiResponse<InboxListItem>>(
    `/admin/lc-crm/inbox/${intakeId}/session`,
    payload,
  )
  return res.data.data!
}

export async function updateIntakeAttendance(intakeId: number, attendanceStatus: string) {
  const res = await apiClient.patch<ApiResponse<{ intake: InboxListItem; ledgerSynced: boolean }>>(
    `/admin/lc-crm/inbox/${intakeId}/attendance`,
    { attendanceStatus },
  )
  return res.data.data!
}

export interface ConversionPreview {
  intakeId: number
  contactId: number
  contactName: string
  existingLeadId?: number | null
  willCreateNewLead: boolean
  previewToken: string
  expiresAt: string
}

export interface ConversionResult {
  leadId: number
  contactId: number
  isNewLead: boolean
}

export async function fetchInbox(filter: InboxFilter) {
  const res = await apiClient.get<ApiResponse<PageResponse<InboxListItem>>>(
    `/admin/lc-crm/inbox${query({
      inboxStatus: filter.inboxStatus,
      sourceType: filter.sourceType,
      applicationStatus: filter.applicationStatus,
      attendanceStatus: filter.attendanceStatus,
      sido: filter.sido,
      search: filter.search,
      page: filter.page ?? 0,
      size: filter.size ?? 20,
    })}`,
  )
  return res.data.data!
}

/**
 * FR-16 CSV 내려받기. `includeOriginal`은 원본 PII를 포함하며 서버가 `EXPORT_WITH_ORIGINAL`
 * audit을 남긴다 (설계 §19.5). 별도 권한이 아니라 추적으로 관리한다.
 */
export async function exportInboxCsv(
  filter: InboxFilter,
  includeOriginal: boolean,
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiClient.get<Blob>(
    `/admin/lc-crm/inbox/export${query({
      inboxStatus: filter.inboxStatus,
      sourceType: filter.sourceType,
      applicationStatus: filter.applicationStatus,
      attendanceStatus: filter.attendanceStatus,
      sido: filter.sido,
      search: filter.search,
      includeOriginal,
    })}`,
    { responseType: 'blob' },
  )
  const today = new Date().toISOString().slice(0, 10)
  return {
    blob: res.data,
    filename:
      extractFilename((res.headers as Record<string, string>)['content-disposition']) ??
      `lc-crm-inbox-${today}.csv`,
  }
}

export async function createManualIntake(payload: {
  name: string
  phone: string
  sourceType?: string
  acquisitionChannel?: AcquisitionChannel | null
  sido?: string
  sigungu?: string
  notes?: string
}) {
  const res = await apiClient.post<ApiResponse<InboxListItem>>(
    '/admin/lc-crm/inbox/manual',
    payload,
  )
  return res.data.data!
}

export interface ManualIntakeUpdatePayload {
  name: string
  notes: string | null
  /** Omission/null preserves the current value. To remove it, send clearAcquisitionChannel. */
  acquisitionChannel?: AcquisitionChannel | null
  clearAcquisitionChannel?: boolean
}

export async function updateManualIntake(intakeId: number, payload: ManualIntakeUpdatePayload) {
  const res = await apiClient.patch<ApiResponse<InboxDetail>>(
    `/admin/lc-crm/inbox/${intakeId}/manual`, payload,
  )
  return res.data.data!
}

export interface ContactNoteItem {
  noteId: number
  contactId: number
  content: string
  authorUserId: number
  createdAt: string
  version: number
  updatedAt?: string | null
  updatedByUserId?: number | null
}

export async function fetchContactNotes(contactId: number) {
  const res = await apiClient.get<ApiResponse<ContactNoteItem[]>>(`/admin/lc-crm/contacts/${contactId}/notes`)
  return res.data.data!
}

export async function createContactNote(contactId: number, content: string) {
  const res = await apiClient.post<ApiResponse<ContactNoteItem>>(`/admin/lc-crm/contacts/${contactId}/notes`, { content })
  return res.data.data!
}

export async function updateContactNote(
  contactId: number,
  noteId: number,
  payload: { content: string; expectedVersion: number },
) {
  const res = await apiClient.patch<ApiResponse<ContactNoteItem>>(
    `/admin/lc-crm/contacts/${contactId}/notes/${noteId}`,
    payload,
  )
  return res.data.data!
}

export async function previewConversion(intakeId: number) {
  const res = await apiClient.post<ApiResponse<ConversionPreview>>(
    `/admin/lc-crm/inbox/${intakeId}/convert/preview`,
  )
  return res.data.data!
}

export async function applyConversion(previewToken: string) {
  const res = await apiClient.post<ApiResponse<ConversionResult>>(
    '/admin/lc-crm/inbox/convert/apply',
    { previewToken },
  )
  return res.data.data!
}

// ---------------------------------------------------------------- 접수 고객

/**
 * 접수 고객 — 연락처 하나로 식별되는 한 사람과 그 사람의 접수 묶음 (ADR-0008).
 * 접수함이 보여주고 세는 단위다. 접수(건)는 상세의 이력으로만 보인다.
 */
export interface IntakeCustomerItem {
  contactId: number
  name: string
  phoneNormalized: string
  sido?: string | null
  sigungu?: string | null
  franchiseTypes?: string[]
  /** BRIEFING_ONLY | FRANCHISE_ONLY | BOTH | OTHER */
  kind: string
  briefingCount: number
  franchiseCount: number
  otherCount: number
  intakeCount: number
  activeBriefingCount: number
  latestReceivedAt: string
  latestSessionDate?: string | null
  latestSessionMode?: string | null
  /** ATTENDED | SCHEDULED | NO_SHOW | UNKNOWN — 접수 고객 참석 요약 */
  attendanceSummary: string
  hasActiveLead: boolean
  leadId?: number | null
  leadStage?: string | null
  isTest: boolean
}

export interface IntakeCustomerFilter {
  kind?: string
  attendanceSummary?: string
  hasLead?: boolean
  search?: string
  includeTest?: boolean
  page?: number
  size?: number
}

export interface IntakeCustomerDetail {
  customer: IntakeCustomerItem
  intakes: InboxDetail[]
}

export interface IntakeCustomerBreakdown {
  total: number
  briefingOnly: number
  franchiseOnly: number
  both: number
  other: number
}

function customerQuery(filter: IntakeCustomerFilter) {
  return {
    kind: filter.kind,
    attendanceSummary: filter.attendanceSummary,
    hasLead: filter.hasLead,
    search: filter.search,
    includeTest: filter.includeTest ? true : undefined,
  }
}

export async function fetchIntakeCustomers(filter: IntakeCustomerFilter) {
  const res = await apiClient.get<ApiResponse<PageResponse<IntakeCustomerItem>>>(
    `/admin/lc-crm/inbox/customers${query({
      ...customerQuery(filter),
      page: filter.page ?? 0,
      size: filter.size ?? 20,
    })}`,
  )
  return res.data.data!
}

export async function fetchIntakeCustomerBreakdown() {
  const res = await apiClient.get<ApiResponse<IntakeCustomerBreakdown>>(
    '/admin/lc-crm/inbox/customers/breakdown',
  )
  return res.data.data!
}

export async function fetchIntakeCustomerDetail(contactId: number) {
  const res = await apiClient.get<ApiResponse<IntakeCustomerDetail>>(
    `/admin/lc-crm/inbox/customers/${contactId}`,
  )
  return res.data.data!
}

/** 접수 고객 CSV. 목록과 같은 필터가 그대로 내려간다. 원본 payload는 싣지 않는다. */
export async function exportIntakeCustomersCsv(
  filter: IntakeCustomerFilter,
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiClient.get<Blob>(
    `/admin/lc-crm/inbox/customers/export${query(customerQuery(filter))}`,
    { responseType: 'blob' },
  )
  const today = new Date().toISOString().slice(0, 10)
  return {
    blob: res.data,
    filename:
      extractFilename((res.headers as Record<string, string>)['content-disposition']) ??
      `lc-crm-intake-customers-${today}.csv`,
  }
}

// ---------------------------------------------------------------- 리드

export interface LeadListItem {
  leadId: number
  contactId: number
  contactName?: string | null
  phoneNormalized?: string | null
  stage: string
  priority: string
  attendanceStatus: string
  assignedTeam?: string | null
  stageEnteredAt: string
  lastActivityAt: string
  convertedAt: string
  reapplicationCount: number
}

export interface LeadDetail {
  leadId: number
  contactId: number
  contactName?: string | null
  phoneNormalized?: string | null
  stage: string
  priority: string
  stageEnteredAt: string
  lastActivityAt: string
  holdCloseReasonCode?: string | null
  holdCloseReasonNote?: string | null
  closedAt?: string | null
  attendanceStatus: string
  desiredOpenYear?: number | null
  desiredOpenMonth?: number | null
  occupation?: string | null
  educationExperience?: string | null
  educationYears?: number | null
  familyConsent?: boolean | null
  spaceStatus?: string | null
  budgetBand?: string | null
  desiredSido?: string | null
  desiredSigungu?: string | null
  qualificationResult?: string | null
  qualificationNote?: string | null
  qualificationDecidedAt?: string | null
  qualificationDecidedBy?: number | null
  qualificationDecidedByName?: string | null
  disclosureProvidedOn?: string | null
  contractProposedOn?: string | null
  contractSignedOn?: string | null
  contractAmount?: number | null
  expectedOpenOn?: string | null
  confirmedSido?: string | null
  confirmedSigungu?: string | null
  assignedTeam?: string | null
  assignedAdminUserId?: number | null
  assignedSupervisorUserId?: number | null
  reapplicationCount: number
  convertedAt: string
  version: number
}

export interface LeadFilter {
  stage?: string
  /** 이름·전화번호 검색. 접수함과 같은 서버 검색이다. */
  search?: string
  /** 담당자(팀) 부분 일치. */
  assignedTeam?: string
  page?: number
  size?: number
}

export interface BulkStageTarget {
  leadId: number
  expectedVersion: number
}

export interface BulkStageResult {
  toStage: string
  changedCount: number
  leadIds: number[]
}

export interface LeadStageHistoryItem {
  historyId: number
  fromStage: string
  toStage: string
  reasonCode?: string | null
  reasonNote?: string | null
  actorUserId?: number | null
  effectiveAt: string
}

export interface LeadAuditEntry {
  auditId: number
  action: string
  actorUserId?: number | null
  beforeValue?: Record<string, unknown> | null
  afterValue?: Record<string, unknown> | null
  createdAt: string
}

export interface LeadHistory {
  leadId: number
  stageTransitions: LeadStageHistoryItem[]
  auditEntries: LeadAuditEntry[]
}

export interface LeadUpdatePayload {
  attendanceStatus?: string | null
  desiredOpenYear?: number | null
  desiredOpenMonth?: number | null
  occupation?: string | null
  educationExperience?: string | null
  educationYears?: number | null
  familyConsent?: boolean | null
  spaceStatus?: string | null
  budgetBand?: string | null
  desiredSido?: string | null
  desiredSigungu?: string | null
  assignedTeam?: string | null
  assignedAdminUserId?: number | null
  assignedSupervisorUserId?: number | null
  disclosureProvidedOn?: string | null
  contractProposedOn?: string | null
  /** null로 되돌릴 필드명 목록. null 값 필드는 서버가 "건드리지 않음"으로 해석하므로 초기화는 여기로 보낸다. */
  clearFields?: string[]
  expectedVersion: number
}

export async function fetchLeads(filter: LeadFilter) {
  const res = await apiClient.get<ApiResponse<PageResponse<LeadListItem>>>(
    `/admin/lc-crm/leads${query({
      stage: filter.stage,
      search: filter.search,
      assignedTeam: filter.assignedTeam,
      page: filter.page ?? 0,
      size: filter.size ?? 20,
    })}`,
  )
  return res.data.data!
}

export async function fetchLeadDetail(leadId: number) {
  const res = await apiClient.get<ApiResponse<LeadDetail>>(`/admin/lc-crm/leads/${leadId}`)
  return res.data.data!
}

export async function fetchLeadHistory(leadId: number) {
  const res = await apiClient.get<ApiResponse<LeadHistory>>(
    `/admin/lc-crm/leads/${leadId}/history`,
  )
  return res.data.data!
}

export async function changeLeadStage(
  leadId: number,
  payload: { toStage: string; reasonCode?: string; reasonNote?: string; expectedVersion: number },
) {
  const res = await apiClient.post<ApiResponse<LeadDetail>>(
    `/admin/lc-crm/leads/${leadId}/stage`,
    payload,
  )
  return res.data.data!
}

/** FR-23. 리드마다 자기 `expectedVersion`을 싣는다. 전부 아니면 전무다. */
export async function bulkChangeStage(payload: {
  toStage: string
  targets: BulkStageTarget[]
  reasonCode?: string
  reasonNote?: string
}) {
  const res = await apiClient.post<ApiResponse<BulkStageResult>>(
    '/admin/lc-crm/leads/bulk-stage-transitions',
    payload,
  )
  return res.data.data!
}

export async function changeLeadPriority(
  leadId: number,
  payload: { priority: string; expectedVersion: number },
) {
  const res = await apiClient.post<ApiResponse<LeadDetail>>(
    `/admin/lc-crm/leads/${leadId}/priority`,
    payload,
  )
  return res.data.data!
}

/** FR-25. 자동 채점이 아니라 운영자 판정을 기록한다. */
export async function decideQualification(
  leadId: number,
  payload: { result: string; note?: string; expectedVersion: number },
) {
  const res = await apiClient.post<ApiResponse<LeadDetail>>(
    `/admin/lc-crm/leads/${leadId}/qualification`,
    payload,
  )
  return res.data.data!
}

export async function updateLead(leadId: number, payload: LeadUpdatePayload) {
  const res = await apiClient.put<ApiResponse<LeadDetail>>(
    `/admin/lc-crm/leads/${leadId}`,
    payload,
  )
  return res.data.data!
}

export async function holdCloseLead(
  leadId: number,
  payload: { reasonCode: string; reasonNote?: string; expectedVersion: number },
) {
  const res = await apiClient.post<ApiResponse<LeadDetail>>(
    `/admin/lc-crm/leads/${leadId}/hold-close`,
    payload,
  )
  return res.data.data!
}

// ---------------------------------------------------------------- 상담 · 첨부 · 할 일

export interface ConsultationListItem {
  consultationId: number
  leadId: number
  channel: string
  inquiryText?: string | null
  answerText?: string | null
  unansweredText?: string | null
  taskText?: string | null
  occurredAt: string
  authorUserId: number
  createdAt: string
  derivedTaskId?: number | null
}

export interface AttachmentListItem {
  attachmentId: number
  consultationId: number
  storageKey: string
  originalFilename: string
  contentType: string
  fileSize: number
  createdAt: string
  logicalDeletedAt?: string | null
}

export interface TaskListItem {
  taskId: number
  leadId: number
  consultationId?: number | null
  taskText: string
  assignedTeam?: string | null
  assignedUserId?: number | null
  dueDate?: string | null
  status: string
  completedAt?: string | null
  createdAt: string
}

export async function fetchConsultations(leadId: number) {
  const res = await apiClient.get<ApiResponse<ConsultationListItem[]>>(
    `/admin/lc-crm/leads/${leadId}/consultations`,
  )
  return res.data.data!
}

export async function createConsultation(
  leadId: number,
  payload: {
    channel: string
    inquiryText?: string
    answerText?: string
    unansweredText?: string
    taskText?: string
    occurredAt?: string
    taskAssignedTeam?: string
    taskAssignedUserId?: number
    taskDueDate?: string
  },
) {
  const res = await apiClient.post<ApiResponse<ConsultationListItem>>(
    `/admin/lc-crm/leads/${leadId}/consultations`,
    payload,
  )
  return res.data.data!
}

/**
 * 상담 수정. 보낸 필드만 바뀐다.
 *
 * `taskText`는 세 뜻을 구분해야 한다 — 생략은 "안 건드림", 값은 "이걸로 바꿈",
 * `clearTask: true`는 "할 일 없앰"이다. 빈 문자열 하나로는 구분되지 않는다.
 */
export async function updateConsultation(
  consultationId: number,
  payload: {
    channel?: string
    inquiryText?: string
    answerText?: string
    unansweredText?: string
    occurredAt?: string
    taskText?: string
    clearTask?: boolean
    taskAssignedTeam?: string
    taskAssignedUserId?: number
    taskDueDate?: string
  },
) {
  const res = await apiClient.put<ApiResponse<ConsultationListItem>>(
    `/admin/lc-crm/consultations/${consultationId}`,
    payload,
  )
  return res.data.data!
}

export async function fetchAttachments(consultationId: number) {
  const res = await apiClient.get<ApiResponse<AttachmentListItem[]>>(
    `/admin/lc-crm/consultations/${consultationId}/attachments`,
  )
  return res.data.data!
}

/**
 * 첨부 업로드는 3단계다. upload-url 발급 → 받은 URL로 스토리지에 직접 PUT →
 * 등록. 중간 PUT은 우리 API가 아니므로 apiClient(Bearer/X-Tenant-Id)를 태우지 않는다.
 */
export async function uploadAttachment(consultationId: number, file: File) {
  const urlRes = await apiClient.post<
    ApiResponse<{ uploadUrl: string; storageKey: string }>
  >(`/admin/lc-crm/consultations/${consultationId}/attachments/upload-url`, {
    contentType: file.type || 'application/octet-stream',
    originalFilename: file.name,
  })
  const { uploadUrl, storageKey } = urlRes.data.data!

  await axios.put(uploadUrl, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  })

  const res = await apiClient.post<ApiResponse<AttachmentListItem>>(
    `/admin/lc-crm/consultations/${consultationId}/attachments`,
    {
      storageKey,
      originalFilename: file.name,
      contentType: file.type || 'application/octet-stream',
    },
  )
  return res.data.data!
}

export async function fetchAttachmentDownloadUrl(attachmentId: number) {
  const res = await apiClient.get<
    ApiResponse<{ downloadUrl: string; originalFilename: string }>
  >(`/admin/lc-crm/attachments/${attachmentId}/download-url`)
  return res.data.data!
}

export async function deleteAttachment(attachmentId: number) {
  await apiClient.delete<ApiResponse<void>>(`/admin/lc-crm/attachments/${attachmentId}`)
}

export async function fetchTasks(leadId: number) {
  const res = await apiClient.get<ApiResponse<TaskListItem[]>>(
    `/admin/lc-crm/leads/${leadId}/tasks`,
  )
  return res.data.data!
}

export async function createTask(
  leadId: number,
  payload: {
    taskText: string
    assignedTeam?: string
    assignedUserId?: number
    dueDate?: string
    consultationId?: number
  },
) {
  const res = await apiClient.post<ApiResponse<TaskListItem>>(
    `/admin/lc-crm/leads/${leadId}/tasks`,
    payload,
  )
  return res.data.data!
}

export async function updateTaskStatus(taskId: number, status: string) {
  const res = await apiClient.put<ApiResponse<TaskListItem>>(
    `/admin/lc-crm/tasks/${taskId}/status`,
    { status },
  )
  return res.data.data!
}

// ---------------------------------------------------------------- 병합 · 분리

export interface MergeReviewListItem {
  reviewId: number
  contactAId: number
  contactAName?: string | null
  contactBId: number
  contactBName?: string | null
  reviewReason: string
  status: string
  comparisonSnapshot: Record<string, unknown>
  createdAt: string
}

export interface MergeContactProjection {
  contactId: number
  canonicalName: string
  phoneNormalized: string
  sido?: string | null
  sigungu?: string | null
  identityActive: boolean
  intakeCount: number
  leadCount: number
  version: number
}

export interface MergePreview {
  sourceContactId: number
  targetContactId: number
  before: MergeContactProjection
  after: MergeContactProjection
  movingIntakeIds: number[]
  movingLeadIds: number[]
  movingConsultationCount: number
  movingTaskCount: number
  activeLeadConflict: boolean
  previewToken: string
  expiresAt: string
}

export interface SplitPreview {
  contactId: number
  stayingIntakeIds: number[]
  movingIntakeIds: number[]
  newContactName: string
  /** 실행하면 번호의 대표가 새 연락처로 넘어가는지 (전화번호 재활용). */
  transfersPhoneIdentity: boolean
  previewToken: string
  expiresAt: string
}

export interface MergeResult {
  mergedContactId: number
  removedContactId: number
  movedIntakeCount: number
  movedLeadCount: number
}

export interface SplitResult {
  newContactId: number
  originalContactId: number
  movedIntakeCount: number
}

export async function fetchMergeReviews(params: {
  status?: string
  page?: number
  size?: number
}) {
  const res = await apiClient.get<ApiResponse<PageResponse<MergeReviewListItem>>>(
    `/admin/lc-crm/merge/reviews${query({
      status: params.status,
      page: params.page ?? 0,
      size: params.size ?? 20,
    })}`,
  )
  return res.data.data!
}

export async function scanForDuplicates() {
  const res = await apiClient.post<
    ApiResponse<{ scannedContacts: number; flaggedReviews: number }>
  >('/admin/lc-crm/merge/scan')
  return res.data.data!
}

export async function decideMergeReview(
  reviewId: number,
  payload: { approved: boolean; rejectionReason?: string },
) {
  await apiClient.post<ApiResponse<void>>(
    `/admin/lc-crm/merge/reviews/${reviewId}/decide`,
    payload,
  )
}

export async function previewMerge(sourceContactId: number, targetContactId: number) {
  const res = await apiClient.post<ApiResponse<MergePreview>>(
    `/admin/lc-crm/merge/preview${query({ sourceContactId, targetContactId })}`,
  )
  return res.data.data!
}

export async function applyMerge(previewToken: string) {
  const res = await apiClient.post<ApiResponse<MergeResult>>('/admin/lc-crm/merge/apply', {
    previewToken,
  })
  return res.data.data!
}

export async function previewSplit(
  contactId: number,
  intakeIds: number[],
  transferPhoneIdentity = false,
) {
  const res = await apiClient.post<ApiResponse<SplitPreview>>(
    `/admin/lc-crm/merge/contacts/${contactId}/split-preview`,
    { intakeIds, transferPhoneIdentity },
  )
  return res.data.data!
}

export async function applySplit(previewToken: string) {
  const res = await apiClient.post<ApiResponse<SplitResult>>('/admin/lc-crm/merge/split', {
    previewToken,
  })
  return res.data.data!
}

// ---------------------------------------------------------------- 계약

export interface ContractTransition {
  transitionId: number
  leadId: number
  eventType: string
  payloadSchemaVersion: number
  status: string
  occurredAt: string
}

export async function createContractTransition(payload: {
  leadId: number
  contractSignedOn: string
  contractAmount: number
  confirmedSido?: string
  confirmedSigungu?: string
  expectedOpenOn?: string
}) {
  const res = await apiClient.post<ApiResponse<ContractTransition>>(
    '/admin/lc-crm/contracts',
    payload,
  )
  return res.data.data!
}

export async function fetchContractTransition(leadId: number) {
  const res = await apiClient.get<ApiResponse<ContractTransition>>(
    `/admin/lc-crm/contracts/${leadId}`,
  )
  return res.data.data!
}

/** 계약 정보 정정 — 리드의 계약 필드만 고치고 전환 이벤트는 불변으로 남는다. */
export async function correctContract(
  leadId: number,
  payload: {
    contractSignedOn?: string
    contractAmount?: number
    confirmedSido?: string
    confirmedSigungu?: string
    expectedOpenOn?: string
    clearFields?: string[]
    expectedVersion: number
  },
) {
  const res = await apiClient.patch<ApiResponse<{ leadId: number; version: number }>>(
    `/admin/lc-crm/contracts/${leadId}`,
    payload,
  )
  return res.data.data!
}

// ---------------------------------------------------------------- 대시보드

export interface CrmDashboard {
  totalContacts: number
  totalIntakes: number
  totalLeads: number
  uniqueContacts: number
  reapplicantContacts: number
  /** 접수 고객 3분류 — 대표 KPI (ADR-0008). */
  intakeCustomers: IntakeCustomerBreakdown
  /** 접수 고객 참석 요약이 참석인 사람 수. */
  attendedCustomers: number
  stageDistribution: Record<string, number>
  priorityDistribution: Record<string, number>
  attendanceDistribution: Record<string, number>
  desiredOpenMonthDistribution: Record<string, number>
  desiredRegionDistribution: Record<string, number>
  holdCloseReasonDistribution: Record<string, number>
  pendingMergeReviews: number
  openTasks: number
  recentIntakes: InboxListItem[]
  overdueTaskCount: number
}

export interface CrmFunnelStage {
  stage: string
  reachedCount: number
  averageDaysInStage?: number | null
}

/**
 * A→B 전환율. `rate`는 분모가 0이면 null이다. 도달은 누적(뒤 단계에 간 리드는 앞 단계도 지난
 * 것으로 센다)이라 1.0을 넘지 않는다.
 */
export interface CrmFunnelConversion {
  fromStage: string
  toStage: string
  denominator: number
  numerator: number
  rate?: number | null
}

export interface CrmFunnel {
  from?: string | null
  to?: string | null
  cohortSize: number
  stages: CrmFunnelStage[]
  conversions: CrmFunnelConversion[]
  holdCloseReasonDistribution: Record<string, number>
  holdClosedCount: number
  /** cohort 리드의 현재 단계 스냅샷. 보류·종결 포함, 합 = cohortSize. */
  currentStageDistribution: Record<string, number>
}

export async function fetchDashboard() {
  const res = await apiClient.get<ApiResponse<CrmDashboard>>('/admin/lc-crm/dashboard')
  return res.data.data!
}

/** 기간은 KST 업무일이고 `to`는 포함이다. */
export async function fetchFunnel(params: { from?: string; to?: string }) {
  const res = await apiClient.get<ApiResponse<CrmFunnel>>(
    `/admin/lc-crm/dashboard/funnel${query({ from: params.from, to: params.to })}`,
  )
  return res.data.data!
}

// ---------------------------------------------------------------- 리드 원본 문서

export type LeadDocumentType = 'QUALIFICATION' | 'DISCLOSURE' | 'CONTRACT'

export interface LeadDocumentItem {
  documentId: number
  leadId: number
  documentType: LeadDocumentType | string
  originalFilename: string
  contentType: string
  fileSize: number
  uploadedBy?: number | null
  createdAt: string
  logicalDeletedAt?: string | null
}

export async function fetchLeadDocuments(leadId: number, documentType?: LeadDocumentType) {
  const res = await apiClient.get<ApiResponse<LeadDocumentItem[]>>(
    `/admin/lc-crm/leads/${leadId}/documents`,
    { params: documentType ? { documentType } : undefined },
  )
  return res.data.data!
}

/** 상담 첨부와 같은 3단계(upload-url → 스토리지 PUT → 등록). 중간 PUT은 apiClient를 태우지 않는다. */
export async function uploadLeadDocument(leadId: number, documentType: LeadDocumentType, file: File) {
  const contentType = file.type || 'application/octet-stream'
  const urlRes = await apiClient.post<ApiResponse<{ uploadUrl: string; storageKey: string }>>(
    `/admin/lc-crm/leads/${leadId}/documents/upload-url`,
    { documentType, contentType, originalFilename: file.name },
  )
  const { uploadUrl, storageKey } = urlRes.data.data!

  await axios.put(uploadUrl, file, { headers: { 'Content-Type': contentType } })

  const res = await apiClient.post<ApiResponse<LeadDocumentItem>>(
    `/admin/lc-crm/leads/${leadId}/documents`,
    { documentType, storageKey, originalFilename: file.name, contentType },
  )
  return res.data.data!
}

export async function fetchLeadDocumentDownloadUrl(documentId: number) {
  const res = await apiClient.get<ApiResponse<{ downloadUrl: string; originalFilename: string }>>(
    `/admin/lc-crm/lead-documents/${documentId}/download-url`,
  )
  return res.data.data!
}

export async function deleteLeadDocument(documentId: number) {
  await apiClient.delete<ApiResponse<void>>(`/admin/lc-crm/lead-documents/${documentId}`)
}

// ---------------------------------------------------------------- baseline import

export interface CrmImportPreview {
  batchId: string
  sourceRowCount: number
  sourceChecksum: string
  previewReport: Record<string, unknown>
  status: string
}

export interface CrmImportBatch {
  batchId: string
  status: string
  jobState?: string | null
  jobPhase?: string | null
  sourceRowCount: number
  processedCount: number
  totalCount: number
  canonicalChecksum?: string | null
  owner: string
  approver?: string | null
  createdAt: string
  startedAt?: string | null
  appliedAt?: string | null
  completedAt?: string | null
  errorMessage?: string | null
}

export interface CrmImportBatchDetail {
  batch: CrmImportBatch
  previewReport?: Record<string, unknown> | null
  applyReport?: Record<string, unknown> | null
}

export interface CrmImportJob {
  batchId: string
  status: string
  jobState: string
  jobPhase: string
  processedCount: number
  totalCount: number
  canonicalChecksum: string
  artifactExpiresAt?: string | null
}

export async function previewCrmImport(payload: {
  customerFile: File
  callsFile: File
  owner: string
  cutoffAt: string
  expectedCanonicalChecksum?: string
}) {
  const form = new FormData()
  form.append('customerFile', payload.customerFile)
  form.append('callsFile', payload.callsFile)
  form.append('owner', payload.owner)
  form.append('cutoffAt', payload.cutoffAt)
  if (payload.expectedCanonicalChecksum) {
    form.append('expectedCanonicalChecksum', payload.expectedCanonicalChecksum)
  }
  const res = await apiClient.post<ApiResponse<CrmImportPreview>>(
    '/admin/lc-crm/import/preview-upload',
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Excel parsing + legacy/canonical preview는 일반 API의 10초 budget보다 길 수 있다.
      timeout: 60_000,
    },
  )
  return res.data.data!
}

export async function approveCrmImport(batchId: string, approver: string) {
  const res = await apiClient.post<ApiResponse<CrmImportBatch>>(
    `/admin/lc-crm/import/${batchId}/approve`,
    { approver },
  )
  return res.data.data!
}

export async function queueCrmImport(batchId: string, expectedCanonicalChecksum: string) {
  const res = await apiClient.post<ApiResponse<CrmImportJob>>(
    `/admin/lc-crm/import/${batchId}/queue`,
    { expectedCanonicalChecksum },
  )
  return res.data.data!
}

export async function fetchCrmImportBatches() {
  const res = await apiClient.get<ApiResponse<CrmImportBatch[]>>('/admin/lc-crm/import')
  return res.data.data!
}

export async function fetchCrmImportBatch(batchId: string) {
  const res = await apiClient.get<ApiResponse<CrmImportBatchDetail>>(
    `/admin/lc-crm/import/${batchId}`,
  )
  return res.data.data!
}

export async function cancelCrmImport(batchId: string) {
  const res = await apiClient.post<ApiResponse<CrmImportJob>>(`/admin/lc-crm/import/${batchId}/cancel`)
  return res.data.data!
}

// ---------------------------------------------------------------- 접수함 재구축 (ADR-0008)

export interface CrmRebuildLedgerSummary {
  briefingRows: number
  briefingCancelledRows: number
  franchiseRows: number
  expectedContacts: number
  expectedIntakes: number
  briefingOnlyContacts: number
  franchiseOnlyContacts: number
  bothContacts: number
}

export interface CrmRebuildPreview {
  deleteCounts: Record<string, number>
  ledger: CrmRebuildLedgerSummary
  previewToken: string
  expiresAt: string
}

export interface CrmRebuildResult {
  deleted: Record<string, number>
  createdContacts: number
  createdIntakes: number
  createdBriefingIntakes: number
  createdFranchiseIntakes: number
  cancelledIntakes: number
  ledger: CrmRebuildLedgerSummary
  executedAt: string
}

/** 서버 설정 `lc.crm.rebuild.enabled`가 꺼져 있으면 `CRM_REBUILD_DISABLED`(403)다. */
export async function previewCrmRebuild() {
  const res = await apiClient.post<ApiResponse<CrmRebuildPreview>>('/admin/lc-crm/rebuild/preview')
  return res.data.data!
}

/**
 * 재구축 작업 상태. execute는 즉시 RUNNING을 돌려주고 실제 삭제·재생성은 서버 뒤에서 돈다 —
 * ALB 60초 안에 끝난다는 보장이 없어서다(staging 444행 49초). 화면은 DONE/FAILED까지 폴링한다.
 */
export interface CrmRebuildStatus {
  /** IDLE | RUNNING | DONE | FAILED */
  state: string
  startedAt?: string | null
  finishedAt?: string | null
  actorUserId?: number | null
  result?: CrmRebuildResult | null
  errorCode?: string | null
  errorMessage?: string | null
}

/**
 * 실행 요청. preview 이후 CRM 규모가 바뀌었으면 `CRM_REBUILD_STALE`(409), 이미 돌고 있으면
 * `CRM_REBUILD_ALREADY_RUNNING`(409). 응답은 RUNNING 상태이고 결과는 [fetchCrmRebuildStatus]로 받는다.
 */
export async function executeCrmRebuild(previewToken: string) {
  const res = await apiClient.post<ApiResponse<CrmRebuildStatus>>('/admin/lc-crm/rebuild/execute', {
    previewToken,
  })
  return res.data.data!
}

export async function fetchCrmRebuildStatus() {
  const res = await apiClient.get<ApiResponse<CrmRebuildStatus>>('/admin/lc-crm/rebuild/status')
  return res.data.data!
}
